<template>
  <div class="scoreboard" :class="{ visible: isVisible }">
    <div class="scoreboard-header">
      <h2>Free-For-All Scoreboard</h2>
    </div>
    
    <div class="scoreboard-players">
      <div class="player-list">
        <div class="player-headers">
          <div class="player-rank">#</div>
          <div class="player-name">Player</div>
          <div class="player-stats">K</div>
          <div class="player-stats">D</div>
          <div class="player-stats">K/D</div>
          <div class="player-stats">Score</div>
        </div>
        <div 
          v-for="(player, index) in sortedPlayers" 
          :key="player.id"
          class="player-row"
          :class="{ 'current-player': player.id === currentPlayerId }"
        >
          <div class="player-rank">{{ index + 1 }}</div>
          <div class="player-name">{{ player.username }}</div>
          <div class="player-stats">{{ player.kills }}</div>
          <div class="player-stats">{{ player.deaths }}</div>
          <div class="player-stats">{{ (player.kills / Math.max(player.deaths, 1)).toFixed(2) }}</div>
          <div class="player-stats">{{ calculateScore(player) }}</div>
        </div>
      </div>
    </div>
    
    <div class="scoreboard-footer">
      <p>Press Tab to hide</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Scoreboard',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    players: {
      type: Array,
      default: () => []
    },
    currentPlayerId: {
      type: String,
      default: ''
    }
  },
  computed: {
    sortedPlayers() {
      // First deduplicate players by ID (keeping the first occurrence)
      const uniquePlayerIds = new Set();
      const uniquePlayers = this.players.filter(player => {
        if (!player.id || uniquePlayerIds.has(player.id)) {
          // Skip if player has no ID or is a duplicate
          return false;
        }
        uniquePlayerIds.add(player.id);
        return true;
      });
      
      // Then sort the deduplicated players
      return uniquePlayers.sort((a, b) => {
        // Calculate K/D ratios (avoid division by zero)
        const aKD = a.kills / Math.max(a.deaths, 1);
        const bKD = b.kills / Math.max(b.deaths, 1);
        
        // First sort by K/D ratio
        if (bKD !== aKD) {
          return bKD - aKD;
        }
        
        // If K/D is the same, sort by join timestamp (earlier joins first)
        return (a.joinTimestamp || 0) - (b.joinTimestamp || 0);
      });
    }
  },
  methods: {
    calculateScore(player) {
      // Simple scoring formula: 100 points per kill, -25 per death
      return (player.kills * 100) - (player.deaths * 25);
    }
  }
};
</script>

<style scoped>
.scoreboard {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  max-width: 800px;
  background-color: rgba(0, 0, 0, 0.85);
  border-radius: 10px;
  padding: 20px;
  color: white;
  display: none;
  z-index: 100;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}

.scoreboard.visible {
  display: block;
}

.scoreboard-header {
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding-bottom: 15px;
  margin-bottom: 15px;
}

.scoreboard-header h2 {
  margin: 0;
  font-size: 1.8em;
}

.scoreboard-players {
  margin-bottom: 15px;
}

.player-list {
  width: 100%;
}

.player-headers, .player-row {
  display: flex;
  padding: 8px 0;
  align-items: center;
}

.player-headers {
  font-weight: bold;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 10px;
}

.player-rank {
  width: 40px;
  text-align: center;
}

.player-name {
  flex: 1;
  padding: 0 10px;
}

.player-stats {
  width: 50px;
  text-align: center;
}

.player-row {
  transition: background-color 0.2s;
}

.player-row:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.current-player {
  background-color: rgba(255, 255, 255, 0.2);
  font-weight: bold;
}

.scoreboard-footer {
  text-align: center;
  font-size: 0.9em;
  color: rgba(255, 255, 255, 0.6);
}
</style> 